import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import { getArticle } from '@/mocks/articleContent';
import { getSummary } from '@/mocks/articles';
import { MAIN_SITE_URL } from '@/mocks/categories';
import { SITE_URL, SITE_NAME, SITE_LOGO_URL } from '@/constants/site';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = slug ? getArticle(slug) : undefined;

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Header />
        <main className="mx-auto flex min-h-[60vh] w-full max-w-3xl flex-col items-center justify-center px-4 py-20 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-2xl text-primary-700">
            <i className="ri-error-warning-line"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            {t('article.notFoundTitle')}
          </h1>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-600">
            {t('article.notFoundDesc')}
          </p>
          <Link
            to="/"
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-arrow-left-line"></i>
            </span>
            Back to Home
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedArticles = article.related
    .map((s) => getSummary(s))
    .filter((a): a is NonNullable<typeof a> => a !== undefined);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    author: { '@type': 'Organization', name: SITE_NAME, url: `${SITE_URL}/` },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: { '@type': 'ImageObject', url: SITE_LOGO_URL },
    },
    dateModified: '2026-08-20',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/article/${article.slug}`,
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <JsonLd data={articleJsonLd} />

      <main>
        <article className="mx-auto w-full max-w-3xl px-4 py-10 md:px-6 md:py-16">
          {/* Breadcrumb */}
          <nav
            aria-label="Breadcrumb"
            className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500 md:text-sm"
          >
            <Link to="/" className="transition-colors hover:text-primary-700">
              {t('article.breadcrumbHome')}
            </Link>
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-arrow-right-s-line"></i>
            </span>
            <Link to="/#articles" className="transition-colors hover:text-primary-700">
              {t('article.breadcrumbGuides')}
            </Link>
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-arrow-right-s-line"></i>
            </span>
            <span className="text-foreground-400">{article.category}</span>
          </nav>

          <header className="mt-6">
            <span className="inline-flex w-fit rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700">
              {article.category}
            </span>
            <h1 className="mt-4 font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-4xl">
              {article.title}
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              {article.description}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500 md:text-sm">
              <span className="flex items-center gap-1.5">
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-time-line"></i>
                </span>
                {article.readTime}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-calendar-check-line"></i>
                </span>
                {t('article.updated')}: {article.updated}
              </span>
            </div>
          </header>

          {/* Body */}
          <div className="mt-8 space-y-8">
            <p className="text-sm leading-relaxed text-foreground-700 md:text-base">{article.intro}</p>

            {article.sections.map((section, i) => (
              <section key={i}>
                {section.heading && (
                  <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                    {section.heading}
                  </h2>
                )}

                {section.paragraphs?.map((p, j) => (
                  <p
                    key={j}
                    className={section.heading ? 'mt-3 text-sm leading-relaxed text-foreground-700 md:text-base' : 'text-sm leading-relaxed text-foreground-700 md:text-base'}
                  >
                    {p}
                  </p>
                ))}

                {section.list && (
                  <ul className="mt-4 space-y-2.5 text-sm text-foreground-700 md:text-base">
                    {section.list.map((item, j) => (
                      <li key={j} className="flex gap-3">
                        <span className="mt-1.5 flex h-4 w-4 shrink-0 items-center justify-center text-primary-600">
                          <i className="ri-checkbox-circle-line"></i>
                        </span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {section.steps && (
                  <ol className="mt-4 space-y-3 text-sm text-foreground-700 md:text-base">
                    {section.steps.map((step, j) => (
                      <li key={j} className="flex gap-3">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-bold text-primary-700">
                          {j + 1}
                        </span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ol>
                )}

                {section.table && (
                  <div className="mt-5 overflow-x-auto rounded-lg border border-background-200">
                    <table className="w-full min-w-[560px] border-collapse text-left text-sm">
                      <thead>
                        <tr className="bg-secondary-100">
                          {section.table.headers.map((h) => (
                            <th
                              key={h}
                              className="px-4 py-3 font-heading font-bold text-foreground-900"
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {section.table.rows.map((row, r) => (
                          <tr key={r} className="border-t border-background-200">
                            {row.map((cell, c) => (
                              <td
                                key={c}
                                className={`px-4 py-3 ${c === 0 ? 'font-semibold text-foreground-800' : 'text-foreground-700'}`}
                              >
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {section.faq && (
                  <div className="mt-4 space-y-3">
                    {section.faq.map((f) => (
                      <details
                        key={f.q}
                        className="group rounded-lg border border-background-200 bg-background-50"
                      >
                        <summary className="flex cursor-pointer items-center justify-between gap-4 px-5 py-4 text-sm font-semibold text-foreground-900 md:text-base">
                          {f.q}
                          <span className="flex h-5 w-5 shrink-0 items-center justify-center text-foreground-400 transition-transform group-open:rotate-45">
                            <i className="ri-add-line"></i>
                          </span>
                        </summary>
                        <p className="px-5 pb-5 text-sm leading-relaxed text-foreground-600">{f.a}</p>
                      </details>
                    ))}
                  </div>
                )}
              </section>
            ))}

            {/* Related guides */}
            {relatedArticles.length > 0 && (
              <section className="rounded-lg border border-background-200 bg-background-100 p-6">
                <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
                  {t('article.relatedTitle')}
                </h2>
                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {relatedArticles.map((r) => (
                    <Link
                      key={r.slug}
                      to={`/article/${r.slug}`}
                      className="group flex items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3 transition-colors hover:border-primary-300"
                    >
                      <span className="text-sm font-semibold text-foreground-800 group-hover:text-primary-700">
                        {r.title}
                      </span>
                      <span className="flex h-4 w-4 shrink-0 items-center justify-center text-primary-600 transition-transform group-hover:translate-x-0.5">
                        <i className="ri-arrow-right-line"></i>
                      </span>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* CTA */}
            <section className="rounded-lg border border-primary-200 bg-primary-50 p-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
                {t('article.ctaTitle')}
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                {t('article.ctaDesc')}
              </p>
              <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                <a
                  href={MAIN_SITE_URL}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
                >
                  {t('article.ctaMain')}
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-arrow-right-up-line"></i>
                  </span>
                </a>
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-200 whitespace-nowrap"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-arrow-left-line"></i>
                  </span>
                  {t('article.backHome')}
                </Link>
              </div>
            </section>

            <p className="text-xs leading-relaxed text-foreground-400">
              {t('footer.disclaimer')}
            </p>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}